import { ClipboardDocumentCheckIcon, ClipboardIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import Markdown from 'react-markdown'
import { twMerge } from 'tailwind-merge'

import Button from '../Button'

export default function MarkdownViewer({
    markdown,
    markdownClass,
    codeWrapperClass,
    containerClass,
}: {
    markdown: string
    markdownClass?: string
    codeWrapperClass?: string
    containerClass?: string
}) {
    const [copyState, setCopyState] = useState<{ [id: string]: 'idle' | 'done' }>({})

    useEffect(() => {
        const chunks = markdown.split(/(```\w+\s[\s\S]*?```)/g)
        const newCopyState = {}

        chunks.forEach((chunk, index) => {
            if (chunk.startsWith('```')) {
                const id = `chunk-${index}`
                newCopyState[id] = 'idle'
            }
        })

        setCopyState(newCopyState)
    }, [markdown])

    const handleCopyToClipboard = (id: string, content: string) => {
        const cleanedMd = content
            .replace(/```(\w+)\s/, '')
            .replace('```', '')
            .trim()
        navigator.clipboard.writeText(cleanedMd)
        setCopyState((prev) => ({ ...prev, [id]: 'done' }))
        setTimeout(() => {
            setCopyState((prev) => ({ ...prev, [id]: 'idle' }))
        }, 2000)
    }

    return (
        <div className={twMerge('relative markdown-wrapper z-0 leading-6 tracking-wide', containerClass)}>
            <Markdown
                children={markdown}
                components={{
                    p(props) {
                        const { children, className, node, ...rest } = props
                        return (
                            <p {...rest} className={twMerge(className, 'mb-4 last:mb-0')}>
                                {children}
                            </p>
                        )
                    },
                    h1(props) {
                        const { children, className, node, ...rest } = props
                        return (
                            <h1 {...rest} className={twMerge(className, 'text-2xl font-bold mb-4 mt-6 first:mt-0')}>
                                {children}
                            </h1>
                        )
                    },
                    h2(props) {
                        const { children, className, node, ...rest } = props
                        return (
                            <h2 {...rest} className={twMerge(className, 'text-xl font-semibold mb-3 mt-5 first:mt-0')}>
                                {children}
                            </h2>
                        )
                    },
                    h3(props) {
                        const { children, className, node, ...rest } = props
                        return (
                            <h3 {...rest} className={twMerge(className, 'text-lg font-semibold mb-2 mt-4 first:mt-0')}>
                                {children}
                            </h3>
                        )
                    },
                    h4(props) {
                        const { children, className, node, ...rest } = props
                        return (
                            <h4
                                {...rest}
                                className={twMerge(className, 'text-base font-semibold mb-2 mt-3 first:mt-0')}
                            >
                                {children}
                            </h4>
                        )
                    },
                    ol(props) {
                        const { children, className, node, ...rest } = props
                        return (
                            <ol
                                {...rest}
                                className={twMerge(className, 'px-3 list-decimal ml-5 flex flex-col gap-y-1 mb-4')}
                            >
                                {children}
                            </ol>
                        )
                    },
                    ul(props) {
                        const { children, className, node, ...rest } = props
                        return (
                            <ul
                                {...rest}
                                className={twMerge(className, 'px-3 list-disc ml-5 flex flex-col gap-y-1 mb-4')}
                            >
                                {children}
                            </ul>
                        )
                    },
                    blockquote(props) {
                        const { children, className, node, ...rest } = props
                        return (
                            <blockquote
                                {...rest}
                                className={twMerge(
                                    className,
                                    'border-l-4 border-gray-300 pl-4 italic text-gray-700 mb-4'
                                )}
                            >
                                {children}
                            </blockquote>
                        )
                    },
                    code(props) {
                        const { children, className, node, ...rest } = props
                        const match = /language-(\w+)/.exec(className || '')
                        const id = node.position?.start.offset.toString() || 'code-0'

                        return match ? (
                            <div
                                className={twMerge(
                                    'relative z-0 markdown-wrapper overflow-clip group my-4',
                                    codeWrapperClass
                                )}
                            >
                                <pre className='overflow-x-auto p-4 rounded-lg bg-gray-900 text-gray-100'>
                                    <code className='text-sm'>{children}</code>
                                </pre>
                                <div className='absolute top-2 right-2'>
                                    <Button
                                        onClick={() => handleCopyToClipboard(id, String(children))}
                                        className='p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-0 group-hover:opacity-100'
                                    >
                                        {copyState[id] === 'done' ? (
                                            <ClipboardDocumentCheckIcon className='w-4 h-4' />
                                        ) : (
                                            <ClipboardIcon className='w-4 h-4' />
                                        )}
                                    </Button>
                                </div>
                                {copyState[id] === 'done' && (
                                    <span className='absolute top-2 right-12 text-xs text-green-400 opacity-0 group-hover:opacity-100'>
                                        Copied!
                                    </span>
                                )}
                            </div>
                        ) : (
                            <code
                                {...rest}
                                className={twMerge(className, 'bg-gray-100 text-red-600 px-1 py-0.5 rounded text-sm')}
                            >
                                {children}
                            </code>
                        )
                    },
                    hr(props) {
                        const { className, node, ...rest } = props
                        return <hr {...rest} className={twMerge(className, 'my-6 border-gray-300')} />
                    },
                    a(props) {
                        const { children, className, href, node, ...rest } = props
                        return (
                            <a
                                {...rest}
                                href={href}
                                className={twMerge(className, 'text-blue-600 hover:text-blue-800 underline')}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {children}
                            </a>
                        )
                    },
                    strong(props) {
                        const { children, className, node, ...rest } = props
                        return (
                            <strong {...rest} className={twMerge(className, 'font-semibold')}>
                                {children}
                            </strong>
                        )
                    },
                    em(props) {
                        const { children, className, node, ...rest } = props
                        return (
                            <em {...rest} className={twMerge(className, 'italic')}>
                                {children}
                            </em>
                        )
                    },
                }}
            />
        </div>
    )
}
